// @ts-nocheck
import React, { useState } from 'react'
import { Search } from '@mui/icons-material'
import { useContractOperations } from 'src/hooks'
import { useDemoContractAnalysis } from '@/hooks/contracts/useDemoContractAnalysis'
import { useCardComparison } from 'src/contexts/CardComparisonContext'
import { toProperCase } from 'src/utils/Strings'
import { 
  Dialog, 
  DialogPopup, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'

interface Contract {
  contractId: string
  name: string
  type: string
  status?: string
}

interface ContractSelectionPopupProps {
  isOpen: boolean
  onClose: () => void
  onSelectContracts: (contractIds: string[]) => void
  isDemo?: boolean
  excludeCurrentContract?: boolean
  initialSelectedContracts?: string[]
}

export const ContractSelectionPopup: React.FC<ContractSelectionPopupProps> = ({
  isOpen,
  onClose,
  onSelectContracts,
  isDemo = false,
  excludeCurrentContract = true,
  initialSelectedContracts = []
}) => {
  const { musicIndustryContracts } = useDemoContractAnalysis()
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const { currentContractId } = useCardComparison()
  const prevIsOpenRef = React.useRef(isOpen)

  // Initialize selected contracts only when popup first opens (transitions from closed to open)
  React.useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setSelectedContractIds(initialSelectedContracts)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initialSelectedContracts])

  // Use demo data or real data
  const { contracts: realContracts, isLoading } = useContractOperations()
  const contracts = isDemo
    ? musicIndustryContracts.map(c => ({
        contractId: c.contractId,
        name: c.name,
        type: c.type,
        status: 'complete'
      }))
    : realContracts.map(c => ({
        contractId: c.contractId,
        name: c.name,
        type: c.type || 'unknown',
        status: c.status
      }))

  // Filter contracts based on search query, status, and optionally exclude current contract
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contract.type.toLowerCase().includes(searchQuery.toLowerCase())
    const isComplete = !contract.status || contract.status.toLowerCase() === 'complete'
    const isNotCurrentContract = !excludeCurrentContract || contract.contractId !== currentContractId
    return matchesSearch && isComplete && isNotCurrentContract
  })

  const toggleContractSelection = (contractId: string) => {
    setSelectedContractIds(prev =>
      prev.includes(contractId)
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    )
  }

  const handleCompare = () => {
    if (selectedContractIds.length > 0) {
      onSelectContracts(selectedContractIds)
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogPopup className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Select Contracts to Compare</DialogTitle>
          <DialogDescription>
            Choose contracts to compare the same card types with
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
            <Input
              type="text"
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-black/30  text-white"
            />
          </div>
        </div>

        {/* Contract List */}
        <div className="flex-1 overflow-auto max-h-96">
          {isLoading && !isDemo ? (
            <div className="text-center text-white/70 py-8">
              Loading contracts...
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center text-white/70 py-8">
              <p>No contracts found</p>
              <p className="text-sm mt-2">Only completed contracts can be compared</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredContracts.map((contract) => {
                const isSelected = selectedContractIds.includes(contract.contractId)
                return (
                  <label
                    key={contract.contractId}
                    className={`block w-full p-4 rounded-2xl border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-white/10 border-white'
                        : 'bg-black/30 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleContractSelection(contract.contractId)}
                      />
                      <div className="flex-1">
                        <h3 className="text-white font-semibold">{contract.name}</h3>
                        <p className="text-white/60 text-sm">{toProperCase(contract.type)}</p>
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row justify-between items-center">
          <p className="text-white/70">
            {selectedContractIds.length} contract{selectedContractIds.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="ghost"
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white font-medium transition-colors"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompare}
              disabled={selectedContractIds.length === 0}
              className={`px-6 py-2 rounded-full text-white font-medium transition-opacity ${
                selectedContractIds.length === 0
                  ? 'bg-gray-600 opacity-50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#547bb5] to-[#6e53b8] hover:opacity-90'
              }`}
            >
              Compare Selected Contracts
            </Button>
          </div>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  )
}